import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { db, auth } from '../firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';

// ─── Solicitudes de eliminación (sesión 34) ──────────────────────────────────
// Solo el administrador borra (lo exigen las reglas de Firestore). El resto de roles que pueden editar una ficha
// (comercial) piden su eliminación: se guarda en el propio documento el campo
//   solicitud_eliminacion: { motivo, por_uid, por_nombre, fecha: Timestamp }
// El administrador las ve en su Dashboard y, en la ficha, decide: Eliminar (botón de siempre) o Rechazar (campo a null).
// Mismo patrón en leads, presupuestos, clientes y proyectos.

/** Perfil del usuario con sesión (lo provee Layout). */
export const PerfilContext = createContext(null);

const fmtFecha = (f) => {
  const d = f?.toDate ? f.toDate() : (f instanceof Date ? f : null);
  return d ? d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

const est = {
  boton: { background: 'transparent', color: '#BA7517', border: '1px solid #BA7517', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonSec: { background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonPeligro: { background: '#D85A30', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
};

/**
 * Botón "Solicitar eliminación" para quien no es administrador. No aparece si ya hay una solicitud.
 * onCambio(solicitud) se llama tras guardar, para actualizar la ficha y la memoria compartida.
 */
export function BotonSolicitarEliminacion({ coleccion, docId, solicitud, onCambio, nombreRegistro }) {
  const perfil = useContext(PerfilContext);
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  if (solicitud) return null;

  const enviar = async () => {
    if (!motivo.trim()) { setError('Escribe el motivo: ayuda al administrador a decidir.'); return; }
    setGuardando(true); setError('');
    try {
      const nueva = {
        motivo: motivo.trim(),
        por_uid: auth.currentUser?.uid || null,
        por_nombre: perfil?.nombre || auth.currentUser?.email || '',
        fecha: Timestamp.now(),
      };
      await updateDoc(doc(db, coleccion, docId), { solicitud_eliminacion: nueva });
      onCambio(nueva);
      setAbierto(false); setMotivo('');
    } catch (e) {
      console.error('No se pudo solicitar la eliminación', e);
      setError('No se pudo enviar la solicitud. Inténtalo de nuevo.');
    }
    setGuardando(false);
  };

  return (
    <>
      <button style={est.boton} onClick={() => setAbierto(true)}>Solicitar eliminación</button>
      {abierto && createPortal(
        // Portal al <body>: la cabecera fija de las fichas usa backdrop-filter y "encerraba" el diálogo en ella
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
          <div style={{ background: '#1a1440', borderRadius: '12px', padding: '28px', maxWidth: '440px', width: '90%', border: '1px solid rgba(83,74,183,0.3)', boxShadow: '0 24px 48px rgba(0,0,0,0.4)' }}>
            <p style={{ color: '#fff', fontSize: '1rem', fontWeight: '500', marginBottom: '8px' }}>Solicitar eliminación</p>
            <p style={{ color: '#AFA9EC', fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '14px' }}>
              Solo el administrador puede eliminar{nombreRegistro ? ` «${nombreRegistro}»` : ' este registro'}. Le llegará tu solicitud con el motivo y decidirá. Mientras tanto el registro sigue igual.
            </p>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} autoFocus
              placeholder="Motivo (p. ej. duplicado de otro lead, creado por error...)"
              style={{ width: '100%', boxSizing: 'border-box', background: '#0e0b24', border: '1px solid #534AB7', borderRadius: '8px', padding: '10px 14px', color: '#fff', fontSize: '0.9rem', fontFamily: 'DM Sans, sans-serif', resize: 'vertical', outline: 'none' }} />
            {error && <p style={{ color: '#D85A30', fontSize: '0.8rem', marginTop: '8px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={est.botonSec} onClick={() => { setAbierto(false); setError(''); }} disabled={guardando}>Cancelar</button>
              <button style={{ ...est.botonPeligro, background: '#BA7517' }} onClick={enviar} disabled={guardando}>{guardando ? 'Enviando...' : 'Enviar solicitud'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * Aviso en la ficha cuando hay una solicitud pendiente. El administrador rechaza aquí la solicitud (borra el campo;
 * el registro no cambia). Para eliminar usa el botón de siempre de la cabecera: un solo botón de borrar por pantalla
 * (sesión 34, a petición de Orlando: dos botones de eliminar en la misma ficha no dejaban claro qué se borraba).
 */
const NOMBRE_TIPO = { leads: 'lead', presupuestos: 'presupuesto', clientes: 'cliente', proyectos: 'proyecto' };
export function AvisoSolicitudEliminacion({ coleccion, docId, solicitud, esAdmin, onCambio, onEliminar }) {
  const [rechazando, setRechazando] = useState(false);
  if (!solicitud) return null;

  const rechazar = async () => {
    setRechazando(true);
    try {
      await updateDoc(doc(db, coleccion, docId), { solicitud_eliminacion: null });
      onCambio(null);
    } catch (e) { console.error('No se pudo rechazar la solicitud', e); }
    setRechazando(false);
  };

  return (
    <div style={{ margin: '16px 32px 0', background: 'rgba(186,117,23,0.12)', border: '1px solid rgba(186,117,23,0.45)', borderRadius: '10px', padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <p style={{ color: '#BA7517', fontSize: '0.88rem', fontWeight: '500', margin: 0 }}>
          Eliminación solicitada{solicitud.por_nombre ? ` por ${solicitud.por_nombre}` : ''}{solicitud.fecha ? ` · ${fmtFecha(solicitud.fecha)}` : ''}
        </p>
        {solicitud.motivo && <p style={{ color: '#AFA9EC', fontSize: '0.82rem', margin: '4px 0 0' }}>Motivo: {solicitud.motivo}</p>}
        {!esAdmin && <p style={{ color: '#AFA9EC', fontSize: '0.78rem', margin: '4px 0 0' }}>Pendiente de que el administrador la revise.</p>}
        {esAdmin && <p style={{ color: '#AFA9EC', fontSize: '0.78rem', margin: '4px 0 0' }}>
          Si estás de acuerdo, elimínalo con «Eliminar {NOMBRE_TIPO[coleccion] || 'registro'}», arriba a la derecha. Si no, rechaza la solicitud: el {NOMBRE_TIPO[coleccion] || 'registro'} se queda como está.
        </p>}
      </div>
      {esAdmin && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={{ ...est.botonSec, background: '#534AB7', color: '#fff', borderColor: '#534AB7' }} onClick={rechazar} disabled={rechazando}>{rechazando ? 'Rechazando...' : 'Rechazar solicitud'}</button>
        </div>
      )}
    </div>
  );
}
